import { FC, useContext, useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import __ from "src/utils/Translations";
import {
  Controller,
  FormProvider,
  useForm,
  SubmitHandler,
  Path,
} from "react-hook-form";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useCreateArea from "src/Pages/Company/OrganizationStructure/api/useCreateArea";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import { Loader } from "src/Components/Loader/Loader";
import AutocompleteManager from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteManager";
import AutocompleteMainArea from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteMainArea";
import AutocompleteEmployees from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteEmployees";
import {
  ICreateArea,
  IFormData,
} from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import AutocompleteAdditionalArea from "src/Pages/Company/OrganizationStructure/Areas/Components/AutocompleteAdditionalArea";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { createEditAreaScheme } from "src/Pages/Company/OrganizationStructure/Areas/createEditArea.scheme";
import { dynamicNotification } from "src/utils/Notifications";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import {
  BackendError,
  handleBackendErrors,
} from "src/Pages/Company/OrganizationStructure/utils/errors/handleBackendErrors";
import { getErrorMessages } from "src/Pages/Company/OrganizationStructure/utils/errors/errorMessages";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import useUnsavedChangesGuard from "src/Pages/Company/OrganizationStructure/utils/useUnsavedChangesGuard";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";

const CreateArea: FC = () => {
  const formMethods = useForm<IFormData>({
    resolver: standardSchemaResolver(createEditAreaScheme),
    mode: "all",
  });
  const companyId = useCompanyId();
  const history = useHistory();
  const isAhr = useIsAhr();
  const { setModal, resetModal } = useContext(GlobalModalContext);
  const structureId = useStructureStore((state) => state.structureId);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const { control, handleSubmit, setError, getValues } = formMethods;
  const { mutate, error } = useCreateArea(companyId, structureId);
  const logStatus = useLogStatus();

  const unblockRef = useRef<(() => void) | null>(null);
  const nextLocationRef = useRef<any>(null);
  const proceedNavigationRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!error) return;

    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "CreateArea: createArea failed",
      statusDetails: {
        source: "API",
        operation: "createArea",
        status: (error as AxiosError)?.response?.status,
        url: (error as AxiosError)?.config?.url,
      },
      errorObject: error instanceof Error ? error : new Error(String(error)),
    });

    setIsFormSubmitted(false);

    const axiosError = error as AxiosError<{ errors: BackendError[] }>;
    const backendErrors = axiosError.response?.data?.errors || [];
    const processedErrors = handleBackendErrors({
      errors: backendErrors,
      errorMessages: getErrorMessages(),
    });

    processedErrors.forEach(({ field, message }) => {
      setError(field as Path<IFormData>, { type: "server", message });
    });
  }, [error, setError]);

  const onSubmit: SubmitHandler<IFormData> = (formData) => {
    const validationResult = createEditAreaScheme.safeParse(formData);

    if (!validationResult.success) {
      dynamicNotification(__("Błąd walidacji formularza"), "error");
      return;
    }

    const payload: ICreateArea = {
      ...formData,
      ...validationResult.data,
      manager:
        formData.manager?.id === "structure"
          ? null
          : formData.manager?.id ?? null,
      parent: formData.parent?.id || null,
      additionalDisplayArea: formData.additionalDisplayArea?.id || null,
      description: formData.description ?? null,
      members: formData.members?.map((member) => member.id) || [],
      inheritManager: formData.manager == null || formData.manager.id == null,
    };

    setIsFormSubmitted(true);

    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    mutate(payload);
  };

  const confirmLeaving = useUnsavedChangesGuard({
    onExit: () => {
      if (proceedNavigationRef.current) proceedNavigationRef.current();
    },
    setModal,
    resetModal,
  });

  useEffect(() => {
    unblockRef.current = history.block((tx) => {
      if (isFormSubmitted) return undefined;

      const values = getValues();
      const hasUnsavedChanges = Object.values(values).some(
        (v) => v !== "" && v != null
      );

      if (!hasUnsavedChanges) return undefined;

      nextLocationRef.current = tx;

      proceedNavigationRef.current = () => {
        if (unblockRef.current) unblockRef.current();
        history.push(tx.pathname);
      };

      confirmLeaving();

      return false;
    });

    return () => {
      if (unblockRef.current) unblockRef.current();
    };
  }, [history, getValues, confirmLeaving, isFormSubmitted]);

  if (!structureId) {
    return <Loader />;
  }

  return (
    <FormProvider {...formMethods}>
      <Typography
        variant="h4"
        sx={{ fontWeight: "fontWeightBold", textTransform: "uppercase", mb: 3 }}
      >
        {`${__("Tworzenie obszaru")}`}
      </Typography>
      <Paper
        sx={{
          gap: "1.5rem",
          display: "flex",
          flexDirection: "column",
          marginBottom: "1.4rem",
          p: 4,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: "fontWeightBold" }}>
          {__("Dodaj obszar")}
        </Typography>
        <Divider sx={{ m: 0 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              render={() => (
                <ControlledTextField
                  name="name"
                  label={__("Nazwa obszaru")}
                  required
                  normalizeSpaces
                  maxLength={255}
                  data-testid="os__form-name-input"
                />
              )}
            />

            <AutocompleteManager
              companyId={companyId!}
              structureId={structureId}
              helperText={__(
                "Jeżeli pozostawisz to pole puste, to zarządzającym tego obszaru stanie się osoba zarządzająca nadrzędnym obszarem."
              )}
              data-testid="os__form-manager-input"
            />

            <AutocompleteMainArea
              companyId={companyId}
              structureId={structureId}
              data-testid="os__form-area-input"
            />

            <AutocompleteAdditionalArea
              companyId={companyId}
              structureId={structureId}
              data-testid="os__form-additional-area-input"
            />

            <Controller
              name="description"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <ControlledTextField
                  {...field}
                  data-testId="os__edit-form-description-input"
                  fullWidth
                  label={__("Opis")}
                  multiline
                  rows={4}
                  maxLength={1000}
                  showLimitAlways
                />
              )}
            />

            <Typography sx={{ fontWeight: 700 }}>
              {__("Dodawanie pracowników")}:
            </Typography>

            <AutocompleteEmployees
              companyId={companyId}
              structureId={structureId}
              data-testid="os__form-employees-input"
            />

            <Divider sx={{ m: 0 }} />
            <Box sx={{ display: "flex", gap: "24px" }}>
              <Button
                variant="outlined"
                type="button"
                size="large"
                sx={{ flexGrow: 1 }}
                onClick={() =>
                  history.push(
                    isAhr
                      ? `/ahr/organization-structure/configure-structure/structure-areas`
                      : `/company/edit/${companyId}/organization-structure/configure-structure/structure-areas`
                  )
                }
                data-testid="os__form-cancel-button"
              >
                {__("Anuluj")}
              </Button>
              <Button
                variant="contained"
                type="submit"
                size="large"
                sx={{ flexGrow: 1 }}
                data-testid="os__form-submit-button"
              >
                {__("Stwórz obszar")}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </FormProvider>
  );
};

export default CreateArea;
